// The theme is Quanty One's own setting (design-revamp.md section 4a): dark, light or system, chosen
// in the account menu and kept in this origin's localStorage, so the next load (index.html) starts
// in it with no flash. Light is the default, as in AlphaLab. Quanty One cannot follow AlphaLab's
// setting: AlphaLab keeps it in its own localStorage only, which another site cannot read.

export type ThemeSetting = "dark" | "light" | "system";

const KEY = "quanty.theme";
export const THEMES: readonly ThemeSetting[] = ["dark", "light", "system"];

/** The AlphaLab app's origin, built in by the release (package-quanty.sh); undefined in a build without it. */
export function alphalabOrigin(raw: string | undefined = import.meta.env.VITE_ALPHALAB_APP_URL): string | undefined {
  if (!raw) return undefined;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

/** A kept value, or light for none or anything else. */
export function parseTheme(raw: string | null | undefined): ThemeSetting {
  return THEMES.includes(raw as ThemeSetting) ? (raw as ThemeSetting) : "light";
}

export function isDark(setting: ThemeSetting, prefersDark: boolean): boolean {
  return setting === "dark" || (setting === "system" && prefersDark);
}

export function cachedTheme(): ThemeSetting {
  try {
    return parseTheme(localStorage.getItem(KEY));
  } catch {
    return "light";
  }
}

let current: ThemeSetting = "light";
const listeners = new Set<() => void>();

/** The chosen setting, for the menu. */
export function currentTheme(): ThemeSetting {
  return current;
}

/** Whether the page is dark now, for components that pick an asset (the logo). */
export function darkNow(): boolean {
  return document.documentElement.classList.contains("dark");
}

export function onThemeChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function apply(setting: ThemeSetting): void {
  current = setting;
  const media = matchMedia("(prefers-color-scheme: dark)");
  document.documentElement.classList.toggle("dark", isDark(setting, media.matches));
  for (const fn of listeners) fn();
}

/** Chooses a setting: applied now, kept for the next load. */
export function setTheme(setting: ThemeSetting): void {
  try {
    localStorage.setItem(KEY, setting);
  } catch {
    // Kept for this page only.
  }
  apply(setting);
}

/** Applies the kept setting and follows the system's changes. Called once, before the app renders. */
export function startTheme(): void {
  apply(cachedTheme());
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => apply(current));
}
